import { useState, useCallback, useMemo, useEffect } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { notifyProjectFollowers } from "@/services/notificationService";

// ============================================================================
// Types
// ============================================================================

/** Assignment types supported by this hook */
export type AssignmentType =
  | "owner" // assigned_sales_rep_id
  | "manager" // assigned_internal_pm_id
  | "proposal_designer" // assigned_proposal_designer_id
  | "installer_partner" // assigned_installer_partner_id
  | "installer_pm" // assigned_installer_pm_id
  | "surveyor" // assigned_site_surveyor_id
  | "biz_dev" // assigned_biz_dev_rep_id
  | "closer" // assigned_closer_id
  | "setter" // assigned_setter_id
  | "followers"; // project_followers table

/** User type for assignment panels */
export interface AssignmentUser {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  avatar_url?: string;
  phone_number?: string;
  company_name?: string;
  role_id?: string | number;
}

/** Partner type for installer partner assignment */
export interface AssignmentPartner {
  id: string;
  company_name: string;
  user_id?: string;
}

/** Panel open states for all assignment types */
export interface PanelStates {
  owner: boolean;
  manager: boolean;
  proposal_designer: boolean;
  installer_partner: boolean;
  installer_pm: boolean;
  surveyor: boolean;
  biz_dev: boolean;
  closer: boolean;
  setter: boolean;
  followers: boolean;
}

/** Search states for all assignment panels */
export interface SearchStates {
  owner: string;
  manager: string;
  proposal_designer: string;
  installer_partner: string;
  installer_pm: string;
  surveyor: string;
  biz_dev: string;
  closer: string;
  setter: string;
  followers: string;
}

/** Loading states for assignment operations */
export interface LoadingStates {
  owner: boolean;
  manager: boolean;
  proposal_designer: boolean;
  installer_partner: boolean;
  installer_pm: boolean;
  surveyor: boolean;
  biz_dev: boolean;
  closer: boolean;
  setter: boolean;
  followers: boolean;
}

/** Project type for assignment hook (minimal fields needed) */
export interface AssignmentProject {
  id: string;
  project_name?: string;
  project_type?: string;
}

/** Options for the useProjectAssignments hook */
export interface UseProjectAssignmentsOptions {
  projectId: string | null;
  project: AssignmentProject | null;
  onAssignmentChange?: (type: AssignmentType, value: string | null) => void;
  onError?: (error: Error) => void;
}

/** Return type for the useProjectAssignments hook */
export interface UseProjectAssignmentsReturn {
  // Panel states
  panelStates: PanelStates;
  activePanel: AssignmentType | null;
  openPanel: (type: AssignmentType) => void;
  closePanel: (type: AssignmentType) => void;
  togglePanel: (type: AssignmentType) => void;
  closeAllPanels: () => void;

  // Search states
  searchStates: SearchStates;
  setSearch: (type: AssignmentType, value: string) => void;
  clearSearch: (type: AssignmentType) => void;
  clearAllSearches: () => void;

  // Loading states
  loadingStates: LoadingStates;
  isAnyLoading: boolean;
  changingOwner: boolean;

  // Users data
  allUsers: AssignmentUser[];
  eligibleUsers: AssignmentUser[];
  followers: AssignmentUser[];
  setFollowers: React.Dispatch<React.SetStateAction<AssignmentUser[]>>;
  setAllUsers: React.Dispatch<React.SetStateAction<AssignmentUser[]>>;
  setEligibleUsers: React.Dispatch<React.SetStateAction<AssignmentUser[]>>;

  // Fetch functions
  fetchAllUsers: () => Promise<void>;
  fetchFollowers: () => Promise<void>;

  // Assignment handlers
  handleChangeOwner: (userId: string) => Promise<void>;
  handleChangeManager: (userId: string) => Promise<void>;
  handleChangeProposalDesigner: (userId: string) => Promise<void>;
  handleChangeInstallerPartner: (partnerId: string) => Promise<void>;
  handleChangeInstallerPM: (userId: string) => Promise<void>;
  handleChangeSurveyor: (userId: string) => Promise<void>;
  handleChangeBizDev: (userId: string) => Promise<void>;
  handleChangeCloser: (userId: string) => Promise<void>;
  handleChangeSetter: (userId: string) => Promise<void>;

  // Follower handlers
  addFollower: (userId: string) => Promise<void>;
  removeFollower: (userId: string) => Promise<void>;

  // Utility functions
  getAssignmentLabel: (type: AssignmentType) => string;
  getDbFieldName: (type: AssignmentType) => string;
}

// ============================================================================
// Constants
// ============================================================================

/** Default search states - all empty */
const DEFAULT_SEARCH_STATES: SearchStates = {
  owner: "",
  manager: "",
  proposal_designer: "",
  installer_partner: "",
  installer_pm: "",
  surveyor: "",
  biz_dev: "",
  closer: "",
  setter: "",
  followers: "",
};

/** Default loading states - all false */
const DEFAULT_LOADING_STATES: LoadingStates = {
  owner: false,
  manager: false,
  proposal_designer: false,
  installer_partner: false,
  installer_pm: false,
  surveyor: false,
  biz_dev: false,
  closer: false,
  setter: false,
  followers: false,
};

/** Map assignment types to database field names */
const DB_FIELD_MAP: Record<AssignmentType, string> = {
  owner: "assigned_sales_rep_id",
  manager: "assigned_internal_pm_id",
  proposal_designer: "assigned_proposal_designer_id",
  installer_partner: "assigned_installer_partner_id",
  installer_pm: "assigned_installer_pm_id",
  surveyor: "assigned_site_surveyor_id",
  biz_dev: "assigned_biz_dev_rep_id",
  closer: "assigned_closer_id",
  setter: "assigned_setter_id",
  followers: "project_followers", // Not a direct field
};

/** Map assignment types to display labels */
const LABEL_MAP: Record<AssignmentType, string> = {
  owner: "Project Owner",
  manager: "Project Manager",
  proposal_designer: "Proposal Designer",
  installer_partner: "Installation Partner",
  installer_pm: "Installer Project Manager",
  surveyor: "Site Surveyor",
  biz_dev: "Business Development Rep",
  closer: "Closer",
  setter: "Setter",
  followers: "Followers",
};

/** Map assignment types to success messages */
const SUCCESS_MESSAGE_MAP: Record<AssignmentType, string> = {
  owner: "Project owner updated.",
  manager: "Project manager updated.",
  proposal_designer: "Proposal designer updated.",
  installer_partner: "Installation partner updated.",
  installer_pm: "Installer project manager updated.",
  surveyor: "Site surveyor updated.",
  biz_dev: "Business development rep updated.",
  closer: "Assigned closer updated.",
  setter: "Assigned setter updated.",
  followers: "Follower updated.",
};

// ============================================================================
// Hook Implementation
// ============================================================================

export function useProjectAssignments({
  projectId,
  project,
  onAssignmentChange,
  onError,
}: UseProjectAssignmentsOptions): UseProjectAssignmentsReturn {
  const { toast } = useToast();
  const { user } = useAuth();

  // ============================================================================
  // State
  // ============================================================================

  // Panel open state - only one can be open at a time
  const [activePanel, setActivePanel] = useState<AssignmentType | null>(null);

  // Search states for each panel
  const [searchStates, setSearchStates] = useState<SearchStates>(DEFAULT_SEARCH_STATES);

  // Loading states for each assignment operation
  const [loadingStates, setLoadingStates] = useState<LoadingStates>(DEFAULT_LOADING_STATES);

  // Special loading state for owner change (used externally)
  const [changingOwner, setChangingOwner] = useState(false);

  // Users data
  const [allUsers, setAllUsers] = useState<AssignmentUser[]>([]);
  const [eligibleUsers, setEligibleUsers] = useState<AssignmentUser[]>([]);
  const [followers, setFollowers] = useState<AssignmentUser[]>([]);

  // ============================================================================
  // Derived State
  // ============================================================================

  // Derive panel states from activePanel
  const panelStates = useMemo<PanelStates>(
    () => ({
      owner: activePanel === "owner",
      manager: activePanel === "manager",
      proposal_designer: activePanel === "proposal_designer",
      installer_partner: activePanel === "installer_partner",
      installer_pm: activePanel === "installer_pm",
      surveyor: activePanel === "surveyor",
      biz_dev: activePanel === "biz_dev",
      closer: activePanel === "closer",
      setter: activePanel === "setter",
      followers: activePanel === "followers",
    }),
    [activePanel]
  );

  // Check if any loading operation is in progress
  const isAnyLoading = useMemo(
    () => Object.values(loadingStates).some(Boolean) || changingOwner,
    [loadingStates, changingOwner]
  );

  // ============================================================================
  // Panel Control Functions
  // ============================================================================

  const openPanel = useCallback((type: AssignmentType) => {
    setActivePanel(type);
  }, []);

  const closePanel = useCallback(
    (type: AssignmentType) => {
      if (activePanel === type) {
        setActivePanel(null);
      }
    },
    [activePanel]
  );

  const togglePanel = useCallback((type: AssignmentType) => {
    setActivePanel((prev) => (prev === type ? null : type));
  }, []);

  const closeAllPanels = useCallback(() => {
    setActivePanel(null);
  }, []);

  // ============================================================================
  // Search Control Functions
  // ============================================================================

  const setSearch = useCallback((type: AssignmentType, value: string) => {
    setSearchStates((prev) => ({ ...prev, [type]: value }));
  }, []);

  const clearSearch = useCallback((type: AssignmentType) => {
    setSearchStates((prev) => ({ ...prev, [type]: "" }));
  }, []);

  const clearAllSearches = useCallback(() => {
    setSearchStates(DEFAULT_SEARCH_STATES);
  }, []);

  // ============================================================================
  // Utility Functions
  // ============================================================================

  const getAssignmentLabel = useCallback((type: AssignmentType): string => {
    return LABEL_MAP[type] || type;
  }, []);

  const getDbFieldName = useCallback((type: AssignmentType): string => {
    return DB_FIELD_MAP[type] || type;
  }, []);

  // ============================================================================
  // Fetch Functions
  // ============================================================================

  const fetchAllUsers = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("user_profiles")
        .select("id, first_name, last_name, email, avatar_url, phone_number")
        .order("first_name");

      if (error) {
        console.error("Error fetching users:", error);
        return;
      }

      setAllUsers(data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, []);

  const fetchFollowers = useCallback(async () => {
    if (!projectId) return;

    try {
      const { data, error } = await supabase
        .from("project_followers")
        .select("user_id, user_profiles:user_id(id, first_name, last_name, email, avatar_url)")
        .eq("project_id", projectId);

      if (error) {
        console.error("Error fetching followers:", error);
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const followerUsers = (data || [])
        .map((f: any) => f.user_profiles)
        .filter((u: AssignmentUser | null): u is AssignmentUser => Boolean(u));
      setFollowers(followerUsers);
    } catch (error) {
      console.error("Error fetching followers:", error);
    }
  }, [projectId]);

  // ============================================================================
  // Generic Assignment Handler
  // ============================================================================

  const handleAssignmentChange = useCallback(
    async (type: AssignmentType, value: string | null) => {
      if (!projectId) return;

      const fieldName = DB_FIELD_MAP[type];
      if (!fieldName || type === "followers") return;

      // Set loading state
      if (type === "owner") {
        setChangingOwner(true);
      } else {
        setLoadingStates((prev) => ({ ...prev, [type]: true }));
      }

      try {
        const updateValue = value === "none" ? null : value;

        const { error } = await supabase
          .from("projects")
          .update({ [fieldName]: updateValue })
          .eq("id", projectId);

        if (error) {
          toast({
            title: "Error",
            description: error.message,
            variant: "destructive",
          });
          onError?.(new Error(error.message));
          return;
        }

        toast({
          title: "Success",
          description: SUCCESS_MESSAGE_MAP[type],
        });

        // Close the panel
        closePanel(type);

        // Notify parent
        onAssignmentChange?.(type, updateValue);
      } catch (error) {
        console.error(`Error updating ${type}:`, error);
        toast({
          title: "Error",
          description: `Failed to update ${LABEL_MAP[type].toLowerCase()}.`,
          variant: "destructive",
        });
        onError?.(error as Error);
      } finally {
        if (type === "owner") {
          setChangingOwner(false);
        } else {
          setLoadingStates((prev) => ({ ...prev, [type]: false }));
        }
      }
    },
    [projectId, toast, closePanel, onAssignmentChange, onError]
  );

  // ============================================================================
  // Specific Assignment Handlers (for backward compatibility)
  // ============================================================================

  const handleChangeOwner = useCallback(
    async (userId: string) => {
      await handleAssignmentChange("owner", userId);
    },
    [handleAssignmentChange]
  );

  const handleChangeManager = useCallback(
    async (userId: string) => {
      await handleAssignmentChange("manager", userId);
    },
    [handleAssignmentChange]
  );

  const handleChangeProposalDesigner = useCallback(
    async (userId: string) => {
      await handleAssignmentChange("proposal_designer", userId);
    },
    [handleAssignmentChange]
  );

  const handleChangeInstallerPartner = useCallback(
    async (partnerId: string) => {
      await handleAssignmentChange("installer_partner", partnerId);
    },
    [handleAssignmentChange]
  );

  const handleChangeInstallerPM = useCallback(
    async (userId: string) => {
      await handleAssignmentChange("installer_pm", userId);
    },
    [handleAssignmentChange]
  );

  const handleChangeSurveyor = useCallback(
    async (userId: string) => {
      await handleAssignmentChange("surveyor", userId);
    },
    [handleAssignmentChange]
  );

  const handleChangeBizDev = useCallback(
    async (userId: string) => {
      await handleAssignmentChange("biz_dev", userId);
    },
    [handleAssignmentChange]
  );

  const handleChangeCloser = useCallback(
    async (userId: string) => {
      await handleAssignmentChange("closer", userId);
    },
    [handleAssignmentChange]
  );

  const handleChangeSetter = useCallback(
    async (userId: string) => {
      await handleAssignmentChange("setter", userId);
    },
    [handleAssignmentChange]
  );

  // ============================================================================
  // Follower Handlers
  // ============================================================================

  const addFollower = useCallback(
    async (userId: string) => {
      if (!projectId) return;

      setLoadingStates((prev) => ({ ...prev, followers: true }));

      try {
        // Get user's tenant ID
        const { data: userData, error: userError } = await supabase
          .from("user_profiles")
          .select("tenant_id")
          .eq("id", user?.id)
          .single();

        if (userError || !userData?.tenant_id) {
          toast({
            title: "Error",
            description: "Failed to get user tenant information",
            variant: "destructive",
          });
          return;
        }

        const { error } = await supabase.from("project_followers").insert({
          related_entity_type: "project",
          related_entity_id: projectId,
          project_id: projectId,
          user_id: userId,
          tenant_id: userData.tenant_id,
        });

        if (error) {
          toast({
            title: "Error",
            description: error.message,
            variant: "destructive",
          });
          return;
        }

        // Update local state
        const newFollower = allUsers.find((u) => u.id === userId);
        if (newFollower) {
          setFollowers((prev) => [...prev, newFollower]);
        }

        // Log activity
        await supabase.from("activity_logs").insert({
          project_id: projectId,
          user_id: user?.id,
          action_type: "follower_added",
          details: {
            follower: {
              id: userId,
              name: `${newFollower?.first_name || ""} ${newFollower?.last_name || ""}`,
            },
          },
          timestamp: new Date().toISOString(),
        });

        // Notify project followers
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const userMeta = (user as any)?.user_metadata;
          const senderName = user
            ? `${userMeta?.first_name || ""} ${userMeta?.last_name || ""}`.trim() ||
              user.email ||
              "System"
            : "System";
          const followerName =
            `${newFollower?.first_name || ""} ${newFollower?.last_name || ""}`.trim();

          const pipelineUrl = "/app/pipeline-resi";
          const notificationLink = `${pipelineUrl}?projectId=${projectId}&modal=true`;

          await notifyProjectFollowers({
            projectId,
            type: "project_follower",
            projectName: project?.project_name,
            projectType: project?.project_type,
            senderId: user?.id,
            senderName: senderName,
            message: `Added ${followerName} as a follower`,
            link: notificationLink,
            metadata: {
              action: "follower_added",
              follower_id: userId,
              follower_name: followerName,
            },
          });
        } catch (notificationError) {
          console.error("Error sending follower notification:", notificationError);
        }

        toast({ title: "Success", description: "Follower added." });
        closePanel("followers");
      } catch (error) {
        console.error("Error adding follower:", error);
        toast({
          title: "Error",
          description: "Failed to add follower.",
          variant: "destructive",
        });
        onError?.(error as Error);
      } finally {
        setLoadingStates((prev) => ({ ...prev, followers: false }));
      }
    },
    [projectId, user, project, allUsers, toast, closePanel, onError]
  );

  const removeFollower = useCallback(
    async (userId: string) => {
      if (!projectId) return;

      try {
        // Get follower details before removing
        const removedFollower = followers.find((f) => f.id === userId);

        const { error } = await supabase
          .from("project_followers")
          .delete()
          .eq("related_entity_type", "project")
          .eq("related_entity_id", projectId)
          .eq("project_id", projectId)
          .eq("user_id", userId);

        if (error) {
          toast({
            title: "Error",
            description: error.message,
            variant: "destructive",
          });
          return;
        }

        // Update local state
        setFollowers((prev) => prev.filter((f) => f.id !== userId));

        // Log activity
        await supabase.from("activity_logs").insert({
          project_id: projectId,
          user_id: user?.id,
          action_type: "follower_removed",
          details: {
            follower: {
              id: userId,
              name: `${removedFollower?.first_name || ""} ${removedFollower?.last_name || ""}`,
            },
          },
          timestamp: new Date().toISOString(),
        });

        // Notify project followers
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const userMeta = (user as any)?.user_metadata;
          const senderName = user
            ? `${userMeta?.first_name || ""} ${userMeta?.last_name || ""}`.trim() ||
              user.email ||
              "System"
            : "System";
          const followerName =
            `${removedFollower?.first_name || ""} ${removedFollower?.last_name || ""}`.trim();

          const pipelineUrl = "/app/pipeline-resi";
          const notificationLink = `${pipelineUrl}?projectId=${projectId}&modal=true`;

          await notifyProjectFollowers({
            projectId,
            type: "project_follower",
            projectName: project?.project_name,
            projectType: project?.project_type,
            senderId: user?.id,
            senderName: senderName,
            message: `Removed ${followerName} as a follower`,
            link: notificationLink,
            metadata: {
              action: "follower_removed",
              follower_id: userId,
              follower_name: followerName,
            },
          });
        } catch (notificationError) {
          console.error("Error sending follower removal notification:", notificationError);
        }

        toast({ title: "Success", description: "Follower removed." });
      } catch (error) {
        console.error("Error removing follower:", error);
        toast({
          title: "Error",
          description: "Failed to remove follower.",
          variant: "destructive",
        });
        onError?.(error as Error);
      }
    },
    [projectId, user, project, followers, toast, onError]
  );

  // ============================================================================
  // Effects
  // ============================================================================

  // Fetch followers when projectId changes
  useEffect(() => {
    if (projectId) {
      fetchFollowers();
    }
  }, [projectId, fetchFollowers]);

  // Fetch all users when followers panel opens
  useEffect(() => {
    if (activePanel === "followers") {
      fetchAllUsers();
    }
  }, [activePanel, fetchAllUsers]);

  // ============================================================================
  // Return
  // ============================================================================

  return {
    // Panel states
    panelStates,
    activePanel,
    openPanel,
    closePanel,
    togglePanel,
    closeAllPanels,

    // Search states
    searchStates,
    setSearch,
    clearSearch,
    clearAllSearches,

    // Loading states
    loadingStates,
    isAnyLoading,
    changingOwner,

    // Users data
    allUsers,
    eligibleUsers,
    followers,
    setFollowers,
    setAllUsers,
    setEligibleUsers,

    // Fetch functions
    fetchAllUsers,
    fetchFollowers,

    // Assignment handlers
    handleChangeOwner,
    handleChangeManager,
    handleChangeProposalDesigner,
    handleChangeInstallerPartner,
    handleChangeInstallerPM,
    handleChangeSurveyor,
    handleChangeBizDev,
    handleChangeCloser,
    handleChangeSetter,

    // Follower handlers
    addFollower,
    removeFollower,

    // Utility functions
    getAssignmentLabel,
    getDbFieldName,
  };
}
